import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Modal } from '../components/Modal';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [usePhone, setUsePhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { login, user, loading, resetPassword } = useAuth() as {
    login: (email: string, password: string) => Promise<void>;
    user: any;
    loading: boolean;
    error: any;
    register: (name: string, email: string, password: string) => Promise<{ id: string; email: string | null; username: string; }>;
    signOut: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
  };
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !loading) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return null;
  }

  // Fonction utilitaire pour transformer le numéro en pseudo-email
  function phoneToPseudoEmail(phone: string) {
    return `${phone.replace(/\D/g, '')}@magraine.app`;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    let finalEmail = email;
    if (usePhone) {
      if (!phone.match(/^\+?\d{8,15}$/)) {
        setError('Numéro de téléphone invalide');
        setShowErrorModal(true);
        setIsLoading(false);
        return;
      }
      finalEmail = phoneToPseudoEmail(phone);
    }
    try {
      await login(finalEmail, password);
    } catch (err) {
      setError('Email, numéro ou mot de passe incorrect');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    let finalEmail = email;
    if (usePhone) {
      if (!phone.match(/^\+?\d{8,15}$/)) {
        setError('Numéro de téléphone invalide');
        return;
      }
      finalEmail = phoneToPseudoEmail(phone);
    }
    if (!finalEmail) {
      setError('Veuillez entrer votre email ou numéro de téléphone');
      return;
    }
    try {
      setIsLoading(true);
      await resetPassword(finalEmail);
      setResetSent(true);
      setError('');
    } catch (err) {
      setError("Erreur lors de l'envoi du mail de réinitialisation");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img 
            src="/Nouveau logo magraine_00000.png"
            alt="magraine" 
            className="w-[450px] h-[75px] object-contain"
          />
          <h1 className="text-3xl font-bold text-white/50 mb-2">Connexion</h1>
        </div>

        <div className="flex justify-center mb-2 space-x-2">
          <button
            type="button"
            className={`px-4 py-2 rounded-l-xl ${!usePhone ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-300'}`}
            onClick={() => setUsePhone(false)}
          >
            Email
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-r-xl ${usePhone ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-300'}`}
            onClick={() => setUsePhone(true)}
          >
            Téléphone
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!usePhone ? (
            <div>
              <label className="block text-gray-400 text-sm mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-green-500/50"
                  required={!usePhone}
                  disabled={usePhone}
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-gray-400 text-sm mb-2">Numéro de téléphone</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-green-500/50"
                  required={usePhone}
                  disabled={!usePhone}
                  placeholder="Ex: +237612345678"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-gray-400 text-sm mb-2">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-green-500/50"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}

          {resetSent && (
            <p className="text-green-400 text-sm text-center">
              Un email de réinitialisation a été envoyé à votre adresse
            </p>
          )}

          <button
            type="submit"
            className={`w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 rounded-xl transition-colors ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isLoading}
          >
            {isLoading ? 'Connexion...' : 'Se connecter'}
          </button>

          <button
            type="button"
            onClick={handleResetPassword}
            className="w-full text-gray-400 hover:text-gray-300 text-sm transition-colors"
          >
            Mot de passe oublié ?
          </button>
        </form>
        {showErrorModal && error && (
          <Modal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)}>
            <div className="p-6 text-center">
              <h2 className="text-lg font-bold mb-2 text-red-500">Erreur de connexion</h2>
              <p className="mb-4 text-gray-200">{error}</p>
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-xl font-semibold"
                onClick={() => setShowErrorModal(false)}
              >
                Fermer
              </button>
            </div>
          </Modal>
        )}

        <p className="text-center text-gray-400">
          Pas encore de compte ?{' '}
          <Link to="/register" className="text-green-400 hover:text-green-300 transition-colors">
            S'inscrire
          </Link>
        </p>
      </div>
    </div>
  );
}